<template>
  <div
    class="size-full"
    :class="{ 'relative': isLoading }"
  >
    <b-skeleton-img
      v-if="isLoading"
      class="size-full absolute z-[10] top-0 left-0"
    />
    <div
      ref="container"
      class="size-full"
      :class="{ 'relative z-[1]': isLoading }"
    />
    <client-only>
      <slot />
    </client-only>
  </div>
</template>

<script>
import events from '@packages/mapbox/constants/events.ts'

export default defineNuxtComponent({
  setup () {
    const {
      apiKey: mapboxApiKey,
      style: mapboxStyle,
      version: mapboxVersion,
    } = useRuntimeConfig().public.mapbox

    const mapbox = shallowRef(null)

    return {
      mapboxApiKey,
      mapboxStyle,
      mapboxVersion,
      mapbox,
    }
  },

  expose: [
    'onload',
    'map',
    'waitForMapLoad',
    'fitBounds',
    'resizeMap',
    'panTo',
    'panToAndFit',
    'showPopup',
  ],

  props: {
    /**
     * @prop {Array<[lng, lat]>|Object{lng, lat}} center
     */
    center: {
      type: [Array, Object],
      required: true,
    },

    interactive: {
      type: Boolean,
      default: true,
    },

    /**
     * @see https://docs.mapbox.com/help/glossary/zoom-level
     */
    zoom: {
      type: Number,
      default: 9,
    },

    /**
     * @see https://docs.mapbox.com/mapbox-gl-js/style-spec/projection
     */
    projection: {
      type: String,
      default: 'mercator',
    },

    /**
     * Resize rendered map to parent bounds on resize
     * Could be used to fix white space appearing on map when resizing and doesn't disappear until resizing again
     */
    sizeToParentBounds: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      initiated: false,
      loaded: false,
    }
  },

  computed: {
    gmapToMapboxZoom () {
      return this.zoom - 1
    },

    isLoading () {
      return !this.loaded || !this.initiated
    },

    /**
     * Expose
     */
    map () {
      return this.mapbox
    },
  },

  watch: {
    zoom (newVal) {
      this.mapbox?.jumpTo({ zoom: newVal, duration: 1000 })
    },

    center (newVal) {
      this.mapbox?.panTo(newVal, { duration: 1000 })
    },
  },

  mounted () {
    preloadResource({
      src: `https://api.mapbox.com/mapbox-gl-js/${this.mapboxVersion}/mapbox-gl.css`,
      as: 'css',
    }, 'mapBoxCss')
    loadScript(`https://api.mapbox.com/mapbox-gl-js/${this.mapboxVersion}/mapbox-gl.js`, 'mapBox', this.checkInitialized)
    this.$event.$on(events.mapboxLoaded, this.checkInitialized)
  },

  beforeUnmount () {
    this.resizeObserver?.disconnect()
    this.waitForMapLoadAbortController?.abort()
    this.$event.$off(events.mapboxLoaded, this.checkInitialized)
  },

  created () {
    if (process.browser) {
      this.waitForMapLoadAbortController = new AbortController()
    }
  },

  methods: {
    // if we have multiple instances of Mapboxes on the same page, make sure that only one actually loads the script
    checkInitialized () {
      if (!this.initiated) {
        this.initialize(true)
      }
    },

    initialize () {
      if (!this.$refs.container && !this.initiated) {
        return
      }
      this.initiated = true

      this.$event.$emit(events.mapboxLoaded)
      const MapClass = window?.mapboxgl?.Map

      this.loaded = true
      this.mapbox = !MapClass ? null : new MapClass({
        container: this.$refs.container,
        accessToken: this.mapboxApiKey,
        style: `mapbox://styles/${this.mapboxStyle}`,
        center: this.center,
        zoom: this.gmapToMapboxZoom,
        interactive: this.interactive,
        projection: this.projection,
        attributionControl: false,
      })

      if (this.sizeToParentBounds) {
        this.resizeMap()
        this.resizeObserver = new ResizeObserver(this.resizeMap)
        this.resizeObserver.observe(this.$refs.container)
      }
    },

    async resizeMap () {
      return await this.waitForMapLoad()
        .then(() => {
          this.mapbox?.resize()
        })
        .catch(() => {})
    },

    resize () {
      return this.mapbox?.resize()
    },

    onload (func) {
      this.waitForMapLoad()
        .then(func)
        .catch(() => {})
    },

    async waitForMapLoad () {
      const conditions = () => {
        return this.initiated && this.loaded && this.mapbox && this.mapbox._loaded
      }

      try {
        await promiseUntilCondition(conditions, {
          retires: 200,
          abortSignal: this.waitForMapLoadAbortController?.signal,
        })
      } catch {
        //
      }
    },

    setZoom (zoom, duration = 1000) {
      return this.mapbox?.jumpTo({ zoom, duration })
    },

    panTo (center, options) {
      const zoom = Number(this.getZoom())
      return this.mapbox?.flyTo({ center, zoom, ...(options || {}) })
    },

    async panToAndFit (point, elementHeight) {
      await this.waitForMapLoad()

      const bounds = new window.mapboxgl.LngLatBounds(point, point)
      const currentZoom = this.getZoom()

      let offset = 0
      if (elementHeight) {
        const markerHeight = 70
        const maxLat = 89.99

        // Make element appear centered and below anchor
        offset = (elementHeight + markerHeight) / 2 / 2

        // If too close to top, make element appear below anchor
        const pxToTop = pixelsBetween(point, { lng: point.lng, lat: maxLat }, currentZoom)
        if (elementHeight > pxToTop) {
          // -10 for even better alignment
          offset = Math.abs(offset - 10) * -1
        }
      }

      this.mapbox?.fitBounds(bounds, {
        maxZoom: currentZoom,
        duration: 500,
        padding: 20,
        offset: [0, offset],
      })
    },

    async fitBounds (points, padding = 100) {
      await this.waitForMapLoad()

      const bounds = new window.mapboxgl.LngLatBounds(
        points[0],
        points[0]
      )

      for (const coord of points) {
        bounds.extend(coord)
      }

      try {
        this.mapbox?.fitBounds(bounds, { padding })
      } catch (e) {
        // might want sentry alert here
        // eslint-disable-next-line no-console
        console.error(e)
      }
    },

    getZoom () {
      return this.mapbox?.getZoom()
    },

    getCenter () {
      return this.mapbox?.getCenter()
    },

    showPopup (coordinates, content, options = {}) {
      const popup = new window.mapboxgl.Popup({ closeButton: false, ...options })
        .setLngLat(coordinates)
        .setHTML(content)
        .addTo(this.mapbox)

      return popup
    },
  },
})
</script>
